import React, {
  ComponentType,
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Blurhash } from "react-blurhash";
import Carousel, {
  CommonProps,
  Modal,
  ModalGateway,
  ViewType,
} from "react-images";
import {
  default as _Gallery,
  GalleryI,
  RenderImageProps,
} from "react-photo-gallery";
import styled from "styled-components/macro";
import { IComponent } from "../Component";
import { getAppropriateImage } from "./image";
import { ImageRenderer } from "./ImageRenderer";

export const GALLERY = "gallery";

interface Image {
  baseURL: string;
  width: number;
  height: number;
  dimensionsProcessed?: number[];
  blurHash?: string;
  aspectRatio?: number;
}
export interface GallerySpec {
  type: typeof GALLERY;
  images: Image[];
}

export const ImagePlaceholder = styled.div`
  border: 2px dashed #888;
  border-radius: 15px;
  background: white;
  width: 200px;
  height: 200px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
`;

export const ImageContainer = styled.div`
  display: block;
  max-width: 100%;
`;

export const Photo = ({
  index,
  onClick,
  photo,
  margin,
  direction,
  top,
  left,
}: PropsWithChildren<RenderImageProps<Image>>) => {
  return (
    <div
      style={{
        position: "relative",
        height: photo.height,
        width: photo.width,
        margin: margin,
      }}
    >
      <ImageRenderer
        image={photo}
        onClick={(event) => {
          onClick?.(event, { index });
        }}
      />
    </div>
  );
};

const Gallery = _Gallery as unknown as GalleryI<Image>;

interface LazyViewData extends ViewType {
  source: {
    download?: string;
    regular: string;
    fullscreen?: string;
  };
  blurHash?: string;
  width?: number;
  height?: number;
}

type LazyViewProps = CommonProps & {
  data?: LazyViewData;
  index?: number;
};

const transparentSpacer = (width: number, height: number) =>
  `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"/>`
  )}`;

const viewImageStyle: CSSProperties = {
  height: "auto",
  maxHeight: "100vh",
  maxWidth: "100%",
  userSelect: "none",
};

// Custom carousel view that only fetches the full-resolution image once it is
// the active slide or an immediate neighbour. Non-adjacent slides render their
// blurhash placeholder until navigated near, so opening the lightbox no longer
// downloads every gallery image at full resolution.
const LazyView = ({
  data,
  index = 0,
  currentIndex = 0,
  isFullscreen,
}: LazyViewProps) => {
  const near = Math.abs(index - currentIndex) <= 1;
  // Latch so an image that was fetched stays mounted when navigated away from,
  // avoiding a refetch/decode flash when the user comes back to it.
  const [loaded, setLoaded] = useState(near);

  useEffect(() => {
    if (near) {
      setLoaded(true);
    }
  }, [near]);

  if (!data) {
    return null;
  }

  const source =
    (isFullscreen && data.source.fullscreen) || data.source.regular;

  return (
    <div style={{ position: "relative", lineHeight: 0, textAlign: "center" }}>
      {near || loaded ? (
        <img src={source} style={viewImageStyle} alt={`Image ${index + 1}`} />
      ) : (
        <div style={{ position: "relative", display: "inline-block" }}>
          <img
            src={transparentSpacer(data.width ?? 1, data.height ?? 1)}
            style={{ display: "block", ...viewImageStyle }}
            alt=""
          />
          {data.blurHash ? (
            <div style={{ position: "absolute", inset: 0 }}>
              <Blurhash hash={data.blurHash} width="100%" height="100%" />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const Component = (props: { spec: GallerySpec }) => {
  const [lighboxOpen, setLightboxOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const openLightbox = useCallback((event: any, { index }: any) => {
    setCurrentImage(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = () => {
    setCurrentImage(0);
    setLightboxOpen(false);
  };

  if (props.spec.images.length >= 1) {
    return (
      <>
        <ImageContainer>
          <Gallery
            onClick={openLightbox}
            targetRowHeight={150}
            photos={props.spec.images.map((x) => ({ ...x, src: x.baseURL }))}
            renderImage={(stuff) => <Photo {...stuff} />}
          />
        </ImageContainer>
        {/* @ts-ignore */}
        <ModalGateway>
          {lighboxOpen ? (
            <Modal onClose={closeLightbox}>
              <Carousel
                currentIndex={currentImage}
                components={{
                  View: LazyView as unknown as ComponentType<CommonProps>,
                }}
                views={props.spec.images.map(
                  (i): LazyViewData => ({
                    source: {
                      download: i.baseURL,
                      regular: getAppropriateImage(
                        i,
                        window.innerWidth,
                        window.innerHeight
                      ),
                      fullscreen: getAppropriateImage(
                        i,
                        window.screen.availWidth,
                        window.screen.availHeight
                      ),
                    },
                    blurHash: i.blurHash,
                    width: i.width,
                    height: i.height,
                  })
                )}
              />
            </Modal>
          ) : null}
        </ModalGateway>
      </>
    );
  }
  return <div>Missing Images</div>;
};

export const GalleryComponent: IComponent<GallerySpec> = {
  component: Component,
};
